import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "sps_cart_v2";

const makeCartId = (productId, variant) => `${productId}__${variant}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Hydrate cart from localStorage once on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    setLoaded(true);
  }, []);

  // Persist cart to localStorage AFTER hydration completes, so we don't
  // race-overwrite the saved cart with the initial empty state.
  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, loaded]);

  // product: full product object; variant: { weight, price }
  const addItem = (product, variant, qty = 1) => {
    const v = variant || { weight: product.weight || "200g", price: product.price };
    const cartId = makeCartId(product.id, v.weight);
    setItems((prev) => {
      const existing = prev.find((p) => p.cartId === cartId);
      if (existing) {
        return prev.map((p) =>
          p.cartId === cartId
            ? { ...p, quantity: Math.min(20, p.quantity + qty) }
            : p,
        );
      }
      return [
        ...prev,
        {
          cartId,
          productId: product.id,
          variant: v.weight,
          name: product.name,
          price: v.price,
          weight: v.weight,
          image: product.image,
          quantity: qty,
        },
      ];
    });
    setIsOpen(true);
  };

  const updateQty = (cartId, qty) => {
    setItems((prev) =>
      prev
        .map((p) =>
          p.cartId === cartId
            ? { ...p, quantity: Math.max(0, Math.min(20, qty)) }
            : p,
        )
        .filter((p) => p.quantity > 0),
    );
  };

  const removeItem = (cartId) =>
    setItems((prev) => prev.filter((p) => p.cartId !== cartId));
  const clear = () => setItems([]);

  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const shippingFee = subtotal === 0 ? 0 : subtotal >= 1500 ? 0 : 79;
    return {
      items,
      count,
      subtotal,
      shippingFee,
      total: subtotal + shippingFee,
      isOpen,
      setIsOpen,
      addItem,
      updateQty,
      removeItem,
      clear,
    };
  }, [items, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};

export const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
